import { getScenario } from "./scenarios";
import { useScenario } from "./ScenarioContext";

interface ScenarioPickerProps {
  className?: string;
  /** Optional accessible label override. */
  ariaLabel?: string;
}

/**
 * Chip-row scenario picker. Sits above simulator panels in module pages
 * so the operator can switch program-moments (interview, talk, prayer,
 * baptism). Reads from `<ScenarioProvider>`; no-op when the provider
 * pins a scenario or when only one scenario is available.
 */
export default function ScenarioPicker({ className, ariaLabel }: ScenarioPickerProps) {
  const { scenarioId, available, setScenarioId, pinned } = useScenario();

  if (available.length <= 1 && !pinned) {
    return null;
  }

  return (
    <div
      role="tablist"
      aria-label={ariaLabel ?? "Program scenario"}
      className={className}
      data-scenario-picker={pinned ? "pinned" : "free"}
      style={{
        display: "inline-flex",
        gap: 8,
        alignItems: "center",
        padding: "6px 10px",
        background: "rgba(15, 19, 26, 0.04)",
        border: "1px solid rgba(15, 19, 26, 0.08)",
        borderRadius: 9999,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: 0.4,
          color: "#5d6066",
          textTransform: "uppercase",
          paddingLeft: 4,
        }}
      >
        Scenario
      </span>
      {available.map((id) => {
        const isActive = id === scenarioId;
        const label = getScenario(id).label;
        return (
          <button
            key={id}
            role="tab"
            type="button"
            aria-selected={isActive}
            disabled={pinned}
            onClick={() => setScenarioId(id)}
            style={{
              padding: "5px 11px",
              fontSize: 12,
              fontWeight: 600,
              letterSpacing: 0.2,
              border: "1px solid",
              borderColor: isActive ? "#1f2937" : "rgba(15, 19, 26, 0.16)",
              borderRadius: 9999,
              background: isActive ? "#1f2937" : "#ffffff",
              color: isActive ? "#ffffff" : "#1f2937",
              cursor: pinned ? "not-allowed" : "pointer",
              opacity: pinned && !isActive ? 0.5 : 1,
            }}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}
